import {
  Points,
  PointsMaterial,
  Scene,
  Vector3,
  Raycaster,
} from "three";
import { Common } from "../../../common/src";
import { IQuick3DCamera } from "../camera/quick3d-camera-interface";
import { Quick3DPerspectiveCamera } from "../camera/quick3d-perspective-camera";
import { PointCloudType } from "../pointcloud/loader/interface";
import { Viewer } from "../viewer";
import { PickPoint } from "./PickPoint";
import { PointCloud } from "./PointCloud";
import { Trajectory } from "./Trajectory";

export class ObjectManager {
  private _canvas: HTMLCanvasElement;
  private _viewer: Viewer;
  private _scene: Scene;
  private _pointCloud: PointCloud;
  private _trajectory: Trajectory;
  private _pickPoints: PickPoint[];
  private _pickPointsGuide: PickPoint;

  constructor(scene: Scene, viewer: Viewer) {
    this._scene = scene;
    this._pointCloud = new PointCloud({ threshold: 0.01 });
    this._trajectory = new Trajectory(viewer);
    this._pickPoints = [];
    this._viewer = viewer;
  }

  public setPointCloud(pointCloud: PointCloud) {
    this._pointCloud = pointCloud;
  }

  public setCanvas(canvas: HTMLCanvasElement): void {
    this._canvas = canvas;
  }

  public InitPickPointsGuide(): void {
    let isInit = true;
    this._scene.children.forEach((child) => {
      if (this._pickPointsGuide.getPickPointGroup().uuid === child.uuid)
        isInit = false;
    });
    if (!isInit) return;
    this._pickPointsGuide = new PickPoint("-1", "", this._viewer);
    this._scene.add(this._pickPointsGuide.getPickPointGroup());
    this._pickPointsGuide.getPickPointGroup().visible = false;
  }

  public getPointsMaterial(): PointsMaterial {
    return this._pointCloud.getPointsMaterial();
  }

  public getPointCloud(): Points {
    return this._pointCloud.getMesh();
  }

  public async loadPointCloud(
    url: string,
    type: PointCloudType,
    abortController: AbortController
  ): Promise<void> {
    return new Promise(async (resolve, rejects) => {
      this._pointCloud
        .loadPointCloud(url, type, abortController)
        .then(() => {
          this._scene.add(this._pointCloud.getMesh());
          resolve();
        })
        .catch(() => {
          rejects();
        });
    });
  }

  public async loadTrajectory(
    url: string,
    abortController: AbortController
  ): Promise<void> {
    return new Promise(async (resolve, rejects) => {
      this._trajectory
        .loadTrajectory(url, abortController)
        .then(() => {
          if (this._viewer.getCurrentType() === Common.POINT_CLOUD_TYPE.SIMPLE) {
            this._scene.add(this._trajectory.getMesh());
          } else {
            console.warn("point cloud type is wrong.")
          }
          resolve();
        })
        .catch(() => {
          rejects();
        });
    });
  }

  public addPickPoint(id: string, name: string, position: Vector3): void {
    const pickPoint = new PickPoint(id, name, this._viewer);
    this._scene.add(pickPoint.getPickPointGroup());
    pickPoint.setPosition(position, this._pointCloud.getOffset());
    this._pickPoints.push(pickPoint);
  }

  public updatePickPoint(id: string, name: string, position: Vector3): void {
    this._pickPoints.forEach((item) => {
      if (item.getId() === id) {
        if (name !== null) item.setName(name);
        if (position !== null)
          item.setPosition(position, this._pointCloud.getOffset());
      }
    });
  }

  public deletePickPoint(id: string): void {
    this._pickPoints = this._pickPoints.filter((item) => {
      if (item.getId() !== id) {
        return true;
      } else {
        this._scene.remove(item.getPickPointGroup());
        return false;
      }
    });
  }

  public deleteAllPickPoint(): void {
    this._pickPoints.forEach((item) => {
      this._scene.remove(item.getPickPointGroup());
    });
    this._pickPoints = [];
  }

  public deleteAllObject(): void {
    this._scene.clear();
    this.InitPickPointsGuide();
  }

  public animateText(camera: IQuick3DCamera) {
    const weight =
      camera.position.distanceTo(this._pointCloud.getMesh().position) / 30;
    this._pickPointsGuide.setTextPosition(weight / camera.zoom);
    for (const pickPoint of this._pickPoints) {
      pickPoint.setTextPosition(weight / camera.zoom);
    }
  }

  public setPickPoint(index: number, position: Vector3): void {
    this._pickPoints[index].setPosition(position, this._pointCloud.getOffset());
  }

  public removePointCloud() {
    if (this._scene.children.includes(this._pointCloud.getMesh())) {
      this._scene.remove(this._pointCloud.getMesh());
      this._pointCloud.getMesh().geometry.dispose();
    }
  }

  public togglePointCloud() {
    !this._scene.children.includes(this._pointCloud.getMesh())
      ? this._scene.add(this._pointCloud.getMesh())
      : this._scene.remove(this._pointCloud.getMesh());
  }

  public removeTrajectory() {
    if (this._scene.children.includes(this._trajectory.getMesh())) {
      this._scene.remove(this._trajectory.getMesh());
      this._trajectory.getMesh().geometry.dispose();
    }
  }

  public toggleTrajectory() {
    !this._scene.children.includes(this._trajectory.getMesh())
      ? this._scene.add(this._trajectory.getMesh())
      : this._scene.remove(this._trajectory.getMesh());
  }

  public intersectPointCloud(raycaster: Raycaster): Vector3 {
    const position = this._pointCloud.intersect(raycaster);
    // if (position === null) return null;
    // this._pickPointsGuide.setPosition(position, this._pointCloud.getOffset());
    // this._pickPointsGuide.getPickPointGroup().visible = true;
    return position;
  }

  public getPointCloudOffset(): Vector3 {
    return this._pointCloud.getOffset();
  }

  public togglePickPointGuide(isShow: boolean): void {
    this._pickPointsGuide.getPickPointGroup().visible = isShow;
  }

  public updatePickPointGuide(position: Vector3) {
    if (!position) {
      this._pickPointsGuide.getPickPointGroup().visible = false;
      return;
    }
    this._pickPointsGuide.setPosition(position, this._pointCloud.getOffset());
    this._pickPointsGuide.getPickPointGroup().visible = true;
  }
}
